import * as React from 'react';

import { cn } from '@/design/utils';

/**
 * The platform's own select, styled to match {@link Input}.
 *
 * **Native rather than a rebuilt listbox**, and that is a deliberate choice for this product rather
 * than a shortcut. These controls are used at a desk, often on a touch screen, by somebody who is
 * talking to the person in front of them: the platform's picker is the one their device already
 * knows how to present, it needs no keyboard handling to be written or maintained, it is reachable
 * by a screen reader with no `aria` plumbing at all, and it is the only version that survives a
 * browser the product was never tested in. The one place that argument does NOT hold is the country
 * field, where two hundred and fifty options have to be searchable — and that one has a combobox of
 * its own for exactly that reason.
 *
 * **Its background is opaque, and that is what makes the open list right in Chrome.** Chromium draws
 * the popup itself and takes the base colour from this element's own computed background, so the
 * translucent one this carried composited against the browser's light default: a pale panel with
 * grey rows, in a product whose page behind it was almost black. Firefox does not derive the popup
 * that way, which is why the same markup looked correct there and wrong here. `--control` is opaque,
 * and `tokens.css` names the SAME token on `option`, so the closed control and the open list cannot
 * disagree. On macOS the list is a native menu that ignores both, and follows `color-scheme`.
 */
function NativeSelect({ className, ...props }: React.ComponentProps<'select'>) {
  return (
    <select
      data-slot="native-select"
      className={cn(
        'h-9 w-full min-w-0 rounded-md border border-input bg-control px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
        'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
        'aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
        className,
      )}
      {...props}
    />
  );
}

export { NativeSelect };
